import React, { LegacyRef, useEffect, useRef } from "react";
import indexer from "./types";
import {
  required,
  isValidRegex,
  grtrThanEqTo,
  lessThanEqTo,
} from "../../utils/validation";
import {
  integerExp,
  alphaNumExp,
  whiteSpaceCheck,
  alphabetExp,
} from "../../utils/regex";
import { InputEditCross } from "./InputEditCross";

export interface IInputFieldProps {
  defaultValue?: string; // The defaultValue in input field (pass only if required)
  value?: string; // The value
  placeholder?: string; // placeholder
  prefix?: React.ReactChild; // text/element to show before the input field
  suffix?: React.ReactChild; // text/element to show after the input field
  helpText?: string; // help text
  helpTextColor?: "grey" | "blue" | "warm-grey-eight" | "red"; // the color of help text, takes gray as default
  errorText?: React.ReactNode; // error text
  required?: boolean; // true if is required
  integer?: boolean; // true if only integer can be entered
  alphanum?: boolean; // true if only alphanumeric can be entered
  autoCapitalize?: boolean; // true if input should be automatically capitalized
  float?: RegExp; // number of digits after decimal
  min?: number; // minimum number
  max?: number; // maximum number
  className?: string; // extra classname if needed for module specific style
  type?: string; // type of input
  maxLength?: number; // if there is a max length for the input
  maxLengthBeforeDot?: number; // if there is a max length for the input
  minLength?: number; // if there is a min length for the input
  pattern?: RegExp; // validate any regExp
  id?: string;
  name?: string;
  autoComplete?: string; // used for switching on/off autocomplete
  onChange?: (value: string) => string | void; // function to be called on change. Return string if value needs to be changed from outside
  onChangeEvent?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (error: indexer, event: React.ChangeEvent<HTMLInputElement>) => void; // function to be called on blur, all errors will be available in error object
  onMouseOut?: (value: string) => string | void; // function to be called onMouseOut.
  placeholderClassName: string;
  disabled?: boolean;
  disablePaste?: boolean;
  lable?: boolean;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputMode?: React.HTMLAttributes<HTMLLIElement>["inputMode"];
  inputPattern?: string;
  showLabel?: boolean;
  shouldBeDifferentValue?: string;
  highlightInputField?: boolean;
  AvoidWhiteSpace?: boolean;
  onClick?: Function;
  autofocus?: boolean;
  passErrorTextButHideErrTxt?: boolean;
  step?: string;
  onWheel?: Function;
  alphabetString?: boolean;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  defaultSuffix?: boolean;
  onFocusShowPrefix?: boolean;
  onlyCorssSuffix?: boolean;
  handleEdit?: Function;
  handleCross?: Function;
  disableSuffix?: boolean;
  requiredBlurrDelay?: number;
  setparentFocus?: Function;
  inputRef?: LegacyRef<HTMLInputElement>;
  onInput?: (event: React.FormEvent<HTMLInputElement>) => void;
  disbableFocus?: boolean;
}

const InputField: React.FC<IInputFieldProps> = (props) => {
  // sets the default value of input if present
  // value: value of the input (type = string)
  // setValue: function to set value
  const [value, setValue] = React.useState(props.defaultValue || "");

  // if default value is updated from outside, change it inside also
  React.useEffect(() => {
    if (props.defaultValue !== undefined && value !== props.defaultValue) {
      setValue(props.defaultValue);
    }
  }, [props.defaultValue]);

  React.useEffect(() => {
    if (props.value !== undefined && value !== props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  // when focussed, this helps to colour the border of input_field_cont
  const [focusClass, setFocusClass] = React.useState(
    props?.autofocus ? "focus" : "",
  );
  const [disablePaste, setDisablePaste] = React.useState(false);

  /**
   * @param value The string that needs to be checked for integer
   * @returns If integer need not be checked, then return true.
   * - If integer needs to be checked, then
   * - If value is valid integer, then return true; otherwise, return false
   */
  function isValidInteger(value: string): boolean {
    return (
      !props.integer || (props.integer && !isValidRegex(value, integerExp))
    );
  }

  /**
   * @param value The string that needs to be checked for float
   * @returns If float need not be checked, then return true.
   * - If float needs to be checked, then
   * - if value is valid float, then return true; otherwise, return false
   */
  function isValidFloat(value: string): boolean {
    return !props.float || (props.float && !isValidRegex(value, props.float));
  }

  /**
   * @param value The string that needs to be checked for alphanumeric characters
   * @returns If alphanum need not be checked, then return true.
   * - If alphanum needs to be checked, then
   * - if value is valid alphanum, then return true; otherwise, return false
   */
  function isValidAlphabetString(value: string): boolean {
    return (
      !props.alphabetString ||
      (props.alphabetString && !isValidRegex(value, alphabetExp))
    );
  }

  /**
   * @param value The string that needs to be checked for alphanumeric characters
   * @returns If alphabet with space need not be checked, then return true.
   * - If alphanum needs to be checked, then
   * - if value is valid alphanum, then return true; otherwise, return false
   */

  function isValidAlphaNum(value: string): boolean {
    return (
      !props.alphanum || (props.alphanum && !isValidRegex(value, alphaNumExp))
    );
  }

  /**
   * @param value The string that needs to be checked for maxlength
   * @returns If maxlength need not be checked, then return true.
   * - If maxlength needs to be checked, then
   * - if value has less characters than maxlength, then return true; otherwise, return false
   */
  function isValidMaxLength(value: string): boolean {
    return props.maxLength === undefined || value.length <= props.maxLength;
  }

  function isValidMaxLengthBeforeDot(value: string): boolean {
    return (
      props?.maxLengthBeforeDot === undefined ||
      parseInt(value).toString().length <= props?.maxLengthBeforeDot
    );
  }

  /**
   * @param value The string that needs to be checked for minlength
   * @returns If minlength need not be checked, then return true.
   * - If minlength needs to be checked, then
   * - if value has less characters than minlength, then return true; otherwise, return false
   */
  function isValidMinLength(value: string): boolean {
    return props.minLength === undefined || value.length < props.minLength;
  }

  /**
   * @param value The value that is required
   * @returns If required need not be checked, then return true.
   * - If required needs to be checked, return true if value is not present; false if value is present
   */
  function isRequired(value: any): boolean {
    return !props.required || (props.required && !!required(value));
  }

  /**
   * @param value The value that is to be validated
   * @returns If min need not be checked, then return true.
   * - If min needs to be checked,
   * - throw error if value is string
   * - return true if value < min; false if value >= min
   */
  function isLessThanMin(value: string): boolean {
    if (props.min === undefined) {
      return true;
    }
    if (isNaN(+value)) {
      throw Error("Can't validate non-numerical value for min");
    }
    return !!grtrThanEqTo(+value, props.min);
  }

  /**
   * @param value The value that is to be validated
   * @returns If max need not be checked, then return true.
   * - If max needs to be checked,
   * - throw error if value is string
   * - return true if value > max; false if value <= min
   */
  function isGrtrThanMax(value: string): boolean {
    if (props.max === undefined) {
      return true;
    }
    if (isNaN(+value)) {
      throw Error("Can't validate non-numerical value for max");
    }
    return !!lessThanEqTo(+value, props.max);
  }

  /**
   * @param value The string that needs to be checked for the pattern
   * @returns If pattern need not be checked, then return true.
   * - If pattern needs to be checked, then
   * - if value is valid pattern, then return true; otherwise, return false
   */
  function isValidPattern(value: string): boolean {
    return (
      !props.pattern || (props.pattern && !isValidRegex(value, props.pattern))
    );
  }

  /**
   * @param value input value gets checked with shouldBeDifferentValue-> True
   *  else false
   *  @returns true
   */
  function stringMatching(valueA: string, valueB: string): boolean {
    return (
      !props.shouldBeDifferentValue ||
      (props.shouldBeDifferentValue && valueA === valueB)
    );
  }

  function AvoidWhiteSpace(value: string): boolean {
    return !(
      !props.AvoidWhiteSpace ||
      (props.AvoidWhiteSpace && !isValidRegex(value, whiteSpaceCheck))
    );
  }

  /**
   * This function runs on blur.
   * This removes the focus class on input_field_cont and removes commas.
   * This function calls the onBlur callback function with all the errors.
   */
  function handleBlur(e: React.ChangeEvent<HTMLInputElement>) {
    // setFocusClass("");
    let errorObj: indexer = {};

    if (props.required) {
      errorObj = { ...errorObj, required: isRequired(value) }; // set isRequired error
    }
    if (props.min) {
      errorObj = { ...errorObj, min: isLessThanMin(value) }; // set error for min
    }
    if (props.max) {
      errorObj = { ...errorObj, max: isGrtrThanMax(value) }; // set error for max
    }
    if (props.pattern) {
      errorObj = { ...errorObj, pattern: isValidPattern(value) }; // set error for pattern
    }
    if (props.shouldBeDifferentValue) {
      errorObj = {
        ...errorObj,
        shouldBeDifferentValue: stringMatching(
          props.shouldBeDifferentValue,
          value,
        ),
      }; //set error for string match
    }
    if (props.AvoidWhiteSpace) {
      errorObj = { ...errorObj, hasWhiteSpace: AvoidWhiteSpace(value) }; // set error for pattern
    }
    if (props.minLength) {
      errorObj = { ...errorObj, minLength: isValidMinLength(value) }; // set error for minLength
    }
    if (props.onBlur) {
      props.onBlur(errorObj, e);
    }
  }

  const handleClose = (e: Event) => {
    if (!props?.disableSuffix) {
      setValue("");
      document.getElementById(props?.id).focus();
      props?.handleCross && props?.handleCross(e);
    }
    // props?.onFocus && props.onFocus();
  };
  const handleEdit = (e: Event) => {
    if (!props?.disableSuffix) {
      e.preventDefault();
      e.stopPropagation();
      setFocusClass("focus");
      props?.setparentFocus && props?.setparentFocus(true);
      document.getElementById(props?.id).focus();
      props?.handleEdit && props?.handleEdit(e);
    }
  };

  const handleOnInput = (e: any) => {
    if (e.target.value.length > props.maxLength) {
      e.target.value = e.target.value.slice(0, props.maxLength);
    }
  };

  /**
   * Checks for all the validations and then changes value accordingly
   * @param e The input event
   */
  function handleChange(e: React.FocusEvent<HTMLInputElement>) {
    if (disablePaste) setValue("");
    else {
      let resolvedValue: string;
      if (props.type == "password") {
        e.target.value = e.target.value.split(" ").join("");
      }
      if (
        (e.target.value || e.target.value === "" || e.target.value === "0") &&
        isValidInteger(e.target.value) &&
        isValidFloat(e.target.value) &&
        isValidAlphaNum(e.target.value) &&
        isValidAlphabetString(e.target.value) &&
        isValidMaxLength(e.target.value) &&
        isValidMaxLengthBeforeDot(e.target.value)
      ) {
        // if value needs to be changed then process the new value
        resolvedValue = e.target.value;

        // Auto-capitalize if the prop is set
        if (props.autoCapitalize) {
          resolvedValue = resolvedValue.toUpperCase();
        }
      } else {
        // otherwise process the previous state
        resolvedValue = value;
      }
      if (props.hasOwnProperty("onChange")) {
        const newValue: any = props.onChange(resolvedValue || ""); // call onchange callback

        if (newValue !== undefined) {
          setValue(newValue); // set the new value
        } else {
          setValue(resolvedValue || ""); // set the new value
        }
      } else {
        setValue(e.target.value);
        props.onChangeEvent && props.onChangeEvent(e);
      }
    }
    setDisablePaste(false);
  }

  const handleMouseOut = (e: any) => {
    if (props.onMouseOut) {
      e.currentTarget.blur();
      props.onMouseOut(e.target.value);
    }
  };
  return (
    <div className="col">
      <div className={props.className}>
        <div
          className={
            props.placeholderClassName +
            (props.errorText ? ` error ${focusClass}` : ` ${focusClass}`)
          }
        >
          {value === "" && props.lable && (
            <>
              <label htmlFor={props.id}>{props.placeholder}</label>
              {props.required && <span className="star">*</span>}
            </>
          )}
          {value != "" && props.showLabel && (
            <>
              <label htmlFor={props.id}>{props.placeholder}</label>
            </>
          )}
        </div>
        {props?.prefix && (
          <>
            {(props?.onFocusShowPrefix === undefined ||
              (props?.onFocusShowPrefix && value !== "")) && (
              <div className="input_prefix">{props.prefix}</div>
            )}
          </>
        )}
        <input
          ref={props.inputRef}
          defaultValue={props.defaultValue ? props.defaultValue : value}
          value={props?.value ? props?.value : value}
          step={props?.step}
          max={props?.max}
          inputMode={props?.inputMode}
          maxLength={props.maxLength}
          minLength={props.minLength}
          onChange={(e: React.FocusEvent<HTMLInputElement>) => {
            focusClass !== "focus" && setFocusClass("focus");
            focusClass !== "focus" &&
              props?.setparentFocus &&
              props?.setparentFocus(true);
            handleChange(e);
          }}
          placeholder={props.lable ? "" : props.placeholder}
          className={`form-control ${
            props.highlightInputField &&
            props.errorText &&
            !props?.defaultSuffix &&
            !props?.suffix
              ? "RedBorder"
              : ""
          } ${
            (value !== "" && props.showLabel) ||
            (value !== "" && props.highlightInputField)
              ? "focus"
              : ""
          }`}
          onWheel={(e) => props?.onWheel && props?.onWheel(e)}
          name={props.name}
          autoFocus={props.autofocus}
          onFocus={(e) => {
            if (props?.disbableFocus) {
              e.target.blur();
              return;
            }
            setFocusClass("focus");
            props?.setparentFocus && props?.setparentFocus(true);
            if (props.onFocus) {
              props.onFocus();
            }
          }}
          onMouseOut={handleMouseOut}
          onBlur={(e) => {
            if (props?.requiredBlurrDelay) {
              setTimeout(() => {
                props.onBlur && handleBlur(e);
                setFocusClass("");
                props?.setparentFocus && props?.setparentFocus(false);
              }, props?.requiredBlurrDelay);
            } else {
              props.onBlur && handleBlur(e);
              setFocusClass("");
              props?.setparentFocus && props?.setparentFocus(false);
            }
          }}
          type={props.type}
          id={props.id}
          disabled={
            props.disabled ||
            (props?.defaultSuffix &&
              !props?.onlyCorssSuffix &&
              value !== "" &&
              focusClass !== "focus")
          }
          autoComplete={props.autoComplete}
          onPaste={() => {
            if (props.disablePaste) setDisablePaste(true);
          }}
          required={props.required}
          pattern={props.inputPattern}
          onClick={() => {
            props?.onClick && props?.onClick();
          }}
          {...(props.onKeyDown ? { onKeyDown: (e) => props.onKeyDown(e) } : {})}
          {...(props.onKeyUp ? { onKeyUp: (e) => props.onKeyUp(e) } : {})}
        />
        {props.children && props.children}

        {(props?.defaultSuffix || props?.suffix) && (
          <div className="suffixes-both">
            {props.defaultSuffix && (
              <>
                {props.showLabel && value !== "" ? (
                  <>
                    <div className="input_suffix textbox-flow">
                      <InputEditCross
                        focused={focusClass}
                        handleClose={handleClose}
                        handleEdit={handleEdit}
                        onlyCross={props?.onlyCorssSuffix}
                        value={value}
                      />
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </>
            )}
            <>
              {" "}
              {props.suffix && (
                <div className="input_suffix second-icon">{props.suffix} </div>
              )}
            </>
          </div>
        )}
      </div>

      {props.passErrorTextButHideErrTxt
        ? null
        : props.errorText && <p className="help_text red">{props.errorText}</p>}
    </div>
  );
};

export default InputField;
